import { Badge } from "@/components/ui/badge";
import { FileSheetLabel } from "@/components/ui/file-sheet-label";
import { RuleChip } from "@/components/ui/rule-chip";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TruncatedText } from "@/components/ui/truncation-tooltip";
import { GoToRowLink } from "@/components/verification/GoToRowLink";
import { fmtNum, fmtRub } from "@/lib/format";
import {
	formatMessage,
	getRuleDisplayLabel,
	reconciliationResultMessages,
} from "@/services/messages";
import type { ActSourceRef, WorkOrderSourceRef } from "@efes/shared";
import { AlertTriangle, XCircle } from "lucide-react";
import { useMemo } from "react";
import { DiscrepancyDeltaPills } from "./DiscrepancyDeltaPills";
import { DiscrepancyRelatedSection } from "./DiscrepancyRelatedSection";
import { SeverityBadge } from "./SeverityBadge";
import { cardClassName, railColor } from "./lib/card-style";
import { extractCodeFromGroupKey } from "./lib/group-key";
import { levelLabel } from "./lib/level-label";
import type { DiscrepancyCardViewModel } from "./lib/view-model";

/**
 * Альтернативная карточка расхождения — Δ-matrix v2 (фича 024 ④).
 *
 * Используется когда сработали несколько правил И среди них есть числовые
 * сравнения (RC-P5 объём, RC-P7 стоимость). Замещает aggregated-вариант
 * `DiscrepancyCard` через `pickCardVariant`.
 *
 * Layout:
 * - severity-rail слева (4px, идентично DiscrepancyCard)
 * - header с **несколькими** rule-chips, кодом, severity badge
 * - Δ-pills row (через `DiscrepancyDeltaPills`)
 * - таблица источников: группа «Наряды» (violet) → группа «Акты» (sky)
 *   → Δ-row с разницей сумм
 *
 * Статичный layout: нет toggle, нет сортировки колонок (порядок WO→ACT смысловой).
 */

const RULE_ORDER: ReadonlyArray<string> = ["RC-P3", "RC-P5", "RC-P7"];

export interface DiscrepancyMatrixCardProps {
	viewModel: DiscrepancyCardViewModel;
	/** ID прогона — пробрасывается для якоря и ключей. */
	runId?: string;
	/** Если true — карточка визуально подсвечена (drill-down target). */
	highlighted?: boolean;
	/** Колбэк клика по drill-down ссылке. */
	onRelatedClick?: (targetId: string) => void;
}

export function DiscrepancyMatrixCard({
	viewModel,
	highlighted = false,
	onRelatedClick,
}: DiscrepancyMatrixCardProps) {
	const messages = reconciliationResultMessages.redesign;

	// Уникальные ruleId из violations, отсортированные согласно `RULE_ORDER`
	// (P3 → P5 → P7 — естественный порядок чтения «наименование → объём → стоимость»).
	// Правила вне списка идут в конец в исходном порядке появления.
	const uniqueRuleIds = useMemo<string[]>(() => {
		const seen = new Set<string>();
		const ordered: string[] = [];
		for (const v of viewModel.violations) {
			if (!seen.has(v.ruleId)) {
				seen.add(v.ruleId);
				ordered.push(v.ruleId);
			}
		}
		return ordered.sort((a, b) => {
			const ia = RULE_ORDER.indexOf(a);
			const ib = RULE_ORDER.indexOf(b);
			if (ia === -1 && ib === -1) return ordered.indexOf(a) - ordered.indexOf(b);
			if (ia === -1) return 1;
			if (ib === -1) return -1;
			return ia - ib;
		});
	}, [viewModel.violations]);

	const code = extractCodeFromGroupKey(viewModel.groupKey);

	// workName — из первого источника (act предпочтительнее, иначе wo).
	const workName =
		viewModel.actSources[0]?.workName ?? viewModel.workOrderSources[0]?.workName ?? "";

	const subtitle = formatMessage(messages.matrix_subtitle_template, {
		rules: uniqueRuleIds.length,
		wo: viewModel.workOrderSources.length,
		act: viewModel.actSources.length,
	});

	// Aggregates по сторонам и Δ.
	// Σ стоимостей — сумма ОКРУГЛЁННЫХ значений (а не Math.round(точная_сумма)),
	// чтобы Σ арифметически сходилась с пользовательским «сложением колонки»:
	// 34 + 34 + 31 600 + 6 340 = 38 008, а не вводящие в заблуждение «38 007»
	// от Math.round(38007.2). Δ-row тогда тоже использует те же округлённые
	// суммы (Δ = sum_act - sum_wo), что согласованно с visual rows.
	// Объёмы — точная сумма (fmtNum показывает десятичные явно).
	const totals = useMemo(() => {
		const woQty = viewModel.workOrderSources.reduce((acc, s) => acc + s.quantity, 0);
		const woCost = viewModel.workOrderSources.reduce((acc, s) => acc + Math.round(s.amount), 0);
		const actQty = viewModel.actSources.reduce((acc, s) => acc + s.quantity, 0);
		const actCost = viewModel.actSources.reduce(
			(acc, s) => acc + Math.round(s.salaryTotal + (s.extraLaborTotal ?? 0)),
			0,
		);
		return {
			woQty,
			woCost,
			actQty,
			actCost,
			dQty: actQty - woQty,
			dCost: actCost - woCost,
		};
	}, [viewModel.actSources, viewModel.workOrderSources]);

	// Единица измерения — из первого источника любой стороны.
	const unit = viewModel.actSources[0]?.unit ?? viewModel.workOrderSources[0]?.unit ?? "";

	// Similarity для inline-badge в acts-rows: первый RC-P3 с metadata.similarity.
	const similarityPercent = useMemo<number | null>(() => {
		for (const v of viewModel.violations) {
			if (v.ruleId !== "RC-P3") continue;
			const raw = v.metadata?.similarity;
			if (typeof raw === "number") {
				return Math.round(raw * 100);
			}
		}
		return null;
	}, [viewModel.violations]);

	// Δ %-вычисления — защищены от деления на ноль.
	const dQtyPercent = totals.woQty !== 0 ? (totals.dQty / totals.woQty) * 100 : null;
	const dCostPercent = totals.woCost !== 0 ? (totals.dCost / totals.woCost) * 100 : null;

	return (
		<article
			id={`discrepancy-${viewModel.id}`}
			aria-label={viewModel.summary}
			className={cardClassName(viewModel.severity, highlighted)}
		>
			<div className="flex">
				<div className={`w-1 ${railColor(viewModel.severity)}`} aria-hidden="true" />
				<div className="min-w-0 flex-1">
					{/* Header */}
					<header className="flex items-start gap-3 px-4 pt-3 pb-2">
						{viewModel.severity === "error" ? (
							<XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
						) : (
							<AlertTriangle
								className="mt-0.5 h-4 w-4 shrink-0 text-yellow-500"
								aria-hidden="true"
							/>
						)}
						<div className="min-w-0 flex-1">
							<div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
								{uniqueRuleIds.map((ruleId) => (
									<RuleChip key={ruleId}>{getRuleDisplayLabel(ruleId)}</RuleChip>
								))}
								{code ? (
									<span className="font-mono text-[11px] text-slate-500">код {code}</span>
								) : null}
								<span className="text-slate-300">·</span>
								<span className="text-[11px] text-slate-500">{levelLabel(viewModel.level)}</span>
								<span className="ml-auto">
									<SeverityBadge severity={viewModel.severity} />
								</span>
							</div>
							<h3 className="mt-1 text-[14px] font-semibold leading-snug text-slate-900">
								{formatMessage(messages.matrix_title_template, { workName })}
							</h3>
							<p className="mt-0.5 text-[12px] text-slate-500">{subtitle}</p>
						</div>
					</header>

					{/* Δ-pills row */}
					<DiscrepancyDeltaPills violations={viewModel.violations} />

					{/* Sources matrix table.
					    `table-fixed` + явные ширины колонок предотвращают горизонтальный
					    overflow при длинных русских наименованиях / именах файлов.
					    Длинный текст truncate-ится через TruncatedText (внутри строк). */}
					<div className="border-t border-slate-100">
						<table className="w-full table-fixed text-[12px]">
							<thead className="bg-slate-50">
								<tr className="border-b border-slate-200">
									<th className="w-[28%] px-3 py-1.5 text-left text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
										{messages.matrix_col_source}
									</th>
									<th className="px-3 py-1.5 text-left text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
										{messages.matrix_col_name}
									</th>
									<th className="w-[12%] whitespace-nowrap px-3 py-1.5 text-right text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
										{messages.matrix_col_volume}
									</th>
									<th className="w-[12%] whitespace-nowrap px-3 py-1.5 text-right text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
										{messages.matrix_col_rate}
									</th>
									<th className="w-[16%] whitespace-nowrap px-3 py-1.5 text-right text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
										{messages.matrix_col_cost}
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{/* WO group header */}
								<tr className="bg-violet-50/40">
									<td
										colSpan={5}
										className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-violet-700"
									>
										{formatMessage(messages.matrix_group_wo_template, {
											n: viewModel.workOrderSources.length,
										})}
									</td>
								</tr>
								{viewModel.workOrderSources.map((s) => (
									<WorkOrderRow key={`wo-${s.fileId}-${s.rowNumber}`} source={s} unit={unit} />
								))}
								{/* WO Σ-row */}
								<tr className="bg-violet-100/50">
									<td className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">
										{messages.matrix_total_wo}
									</td>
									<td />
									<td className="px-3 py-1.5 text-right font-mono font-semibold tabular-nums">
										{fmtNum(totals.woQty)} {unit}
									</td>
									<td />
									<td className="px-3 py-1.5 text-right font-mono font-semibold tabular-nums">
										{fmtRub(totals.woCost)}
									</td>
								</tr>

								{/* ACT group header */}
								<tr className="bg-sky-50/40">
									<td
										colSpan={5}
										className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-sky-700"
									>
										{formatMessage(messages.matrix_group_act_template, {
											n: viewModel.actSources.length,
										})}
									</td>
								</tr>
								{viewModel.actSources.map((s, i) => (
									<ActRow
										key={`act-${s.fileId}-${s.blockId}-${s.lineId ?? s.excelRow ?? i}`}
										source={s}
										unit={unit}
										similarityPercent={similarityPercent}
									/>
								))}
								{/* ACT Σ-row */}
								<tr className="bg-sky-100/50">
									<td className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700">
										{messages.matrix_total_act}
									</td>
									<td />
									<td className="px-3 py-1.5 text-right font-mono font-semibold tabular-nums">
										{fmtNum(totals.actQty)} {unit}
									</td>
									<td />
									<td className="px-3 py-1.5 text-right font-mono font-semibold tabular-nums">
										{fmtRub(totals.actCost)}
									</td>
								</tr>

								{/* Δ-row */}
								<tr className="bg-red-50 border-t-2 border-red-200">
									<td className="px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-red-800">
										{messages.matrix_delta_label}
									</td>
									<td />
									<td className="px-3 py-2 text-right font-mono text-red-800 tabular-nums">
										<div className="font-semibold">
											{signedNum(totals.dQty)} {unit}
										</div>
										{dQtyPercent !== null && uniqueRuleIds.includes("RC-P5") ? (
											<div className="text-[10px] uppercase tracking-wide">
												{formatMessage(messages.matrix_delta_rule_template, {
													percent: signedPercent(dQtyPercent),
													ruleId: "RC-P5",
												})}
											</div>
										) : null}
									</td>
									<td />
									<td className="px-3 py-2 text-right font-mono text-red-800 tabular-nums">
										<div className="font-semibold">{signedRub(totals.dCost)}</div>
										{dCostPercent !== null && uniqueRuleIds.includes("RC-P7") ? (
											<div className="text-[10px] uppercase tracking-wide">
												{formatMessage(messages.matrix_delta_rule_template, {
													percent: signedPercent(dCostPercent),
													ruleId: "RC-P7",
												})}
											</div>
										) : null}
									</td>
								</tr>
							</tbody>
						</table>
					</div>

					{/* Drill-down related */}
					{viewModel.hasRelated && onRelatedClick ? (
						<div className="px-4 pb-3">
							<DiscrepancyRelatedSection
								relatedIds={viewModel.relatedDiscrepancyIds}
								onRelatedClick={onRelatedClick}
							/>
						</div>
					) : null}
				</div>
			</div>
		</article>
	);
}

// ─── Row-компоненты ─────────────────────────────────────────────

function WorkOrderRow({ source, unit }: { source: WorkOrderSourceRef; unit: string }) {
	const rowDisplay = source.excelRow ?? source.rowNumber;
	// Эффективная расценка наряда = rate × k (с учётом коэффициента). Это
	// именно то, что RC-P6 сравнивает с salaryRate акта. k=1 (или null) —
	// показываем просто rate; иначе формула «rate × k» в title даст детали.
	const k = source.k ?? 1;
	const effectiveRate = source.rate * k;
	const sheet = source.sheetName ?? null;
	return (
		<tr className="bg-white hover:bg-violet-50/30">
			<td className="px-3 py-1.5">
				<div className="flex min-w-0 items-center gap-2">
					<SideBadge side="wo" />
					<span className="min-w-0 flex-1 text-slate-700">
						<FileSheetLabel fileName={source.fileName} sheetName={sheet} />
					</span>
					<GoToRowLink
						excelRow={rowDisplay}
						label={String(rowDisplay)}
						fileId={source.fileId}
						sheet={sheet}
						className="inline-flex shrink-0 items-center gap-0.5 font-mono text-[11px] text-blue-600 hover:underline focus:outline-none focus:ring-1 focus:ring-blue-400 rounded"
					/>
				</div>
			</td>
			<td className="px-3 py-1.5 text-slate-700">
				<TruncatedText value={source.workName} />
			</td>
			<td className="px-3 py-1.5 text-right font-mono text-slate-900 tabular-nums whitespace-nowrap">
				{fmtNum(source.quantity)} {source.unit ?? unit}
			</td>
			<td
				className="px-3 py-1.5 text-right font-mono text-slate-900 tabular-nums whitespace-nowrap"
				title={k !== 1 ? `${fmtRub(source.rate)} × ${k} = ${fmtRub(effectiveRate)}` : undefined}
			>
				{fmtRub(effectiveRate)}
			</td>
			<td className="px-3 py-1.5 text-right font-mono text-slate-900 tabular-nums whitespace-nowrap">
				{fmtRub(source.amount)}
			</td>
		</tr>
	);
}

function ActRow({
	source,
	unit,
	similarityPercent,
}: {
	source: ActSourceRef;
	unit: string;
	similarityPercent: number | null;
}) {
	const rowDisplay = source.excelRow ?? source.lineId ?? source.blockNumber;
	const sheet = source.sheetName ?? null;
	return (
		<tr className="bg-white hover:bg-sky-50/30">
			<td className="px-3 py-1.5">
				<div className="flex min-w-0 items-center gap-2">
					<SideBadge side="act" />
					<span className="min-w-0 flex-1 text-slate-700">
						<FileSheetLabel fileName={source.fileName} sheetName={sheet} />
					</span>
					<GoToRowLink
						excelRow={rowDisplay}
						continuationRows={source.continuationRows}
						label={String(rowDisplay)}
						fileId={source.fileId}
						sheet={sheet}
						className="inline-flex shrink-0 items-center gap-0.5 font-mono text-[11px] text-blue-600 hover:underline focus:outline-none focus:ring-1 focus:ring-blue-400 rounded"
					/>
				</div>
			</td>
			<td className="px-3 py-1.5 text-slate-700">
				<div className="flex min-w-0 items-center gap-1.5">
					<span className="min-w-0 flex-1">
						<TruncatedText value={source.workName} />
					</span>
					{similarityPercent !== null ? (
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<span className="shrink-0 rounded bg-yellow-50 px-1 font-mono text-[10px] text-yellow-800 ring-1 ring-inset ring-yellow-200">
										{similarityPercent}%
									</span>
								</TooltipTrigger>
								<TooltipContent>RC-P3 · близость наименований</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					) : null}
				</div>
			</td>
			<td className="px-3 py-1.5 text-right font-mono text-slate-900 tabular-nums whitespace-nowrap">
				{fmtNum(source.quantity)} {source.unit ?? unit}
			</td>
			<td className="px-3 py-1.5 text-right font-mono text-slate-900 tabular-nums whitespace-nowrap">
				{fmtRub(source.salaryRate)}
			</td>
			<td className="px-3 py-1.5 text-right font-mono text-slate-900 tabular-nums whitespace-nowrap">
				{fmtRub(source.salaryTotal)}
			</td>
		</tr>
	);
}

// ─── Helpers ────────────────────────────────────────────────────

function signedNum(n: number): string {
	const sign = n >= 0 ? "+" : "−";
	return `${sign}${fmtNum(Math.abs(n))}`;
}

function signedRub(n: number): string {
	const sign = n >= 0 ? "+" : "−";
	return `${sign}${fmtRub(Math.abs(n))}`;
}

function signedPercent(p: number): string {
	const sign = p >= 0 ? "+" : "−";
	return `${sign}${Math.abs(p).toFixed(1)}`;
}

// Sprint 6 / C11.2: SeverityBadge вынесен в общий модуль ./SeverityBadge.

function SideBadge({ side }: { side: "wo" | "act" }) {
	const messages = reconciliationResultMessages.redesign;
	const cls =
		side === "wo"
			? "bg-violet-50 text-violet-700 ring-violet-200"
			: "bg-sky-50 text-sky-700 ring-sky-200";
	const label = side === "wo" ? messages.matrix_side_badge_wo : messages.matrix_side_badge_act;
	return (
		<Badge
			className={`shrink-0 rounded border-transparent px-1.5 py-0.5 font-mono text-[10px] ring-1 ring-inset ${cls}`}
		>
			{label}
		</Badge>
	);
}
